import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { openConfirmationModal } from '../reducers/uiSlice';
import { confirmationService } from '../services/confirmationService';
import { db } from '../lib/database';
import { toastService } from '../services/toastService';
import { storageService } from '../services/storageService';

export const useSettings = () => {
  const dispatch = useDispatch();
  const [autoSync, setAutoSync] = useState(true);
  const [storageInfo, setStorageInfo] = useState({ usedMB: 0, itemCount: 0 });
  const [loadingAutoSync, setLoadingAutoSync] = useState(false);

  const fetchStorageInfo = () => {
    storageService.getStorageInfo()
      .then(setStorageInfo)
      .catch(() => {});
  };

  useEffect(() => {
    fetchStorageInfo();

    db.settings.get('autoSync')
      .then(setting => {
        if (setting?.value !== undefined) {
          setAutoSync(setting.value);
        }
      })
      .catch(() => {});
  }, []);

  const handleAutoSyncToggle = (newVal) => {
    if (loadingAutoSync) return;
    setLoadingAutoSync(true);
    
    const confirmId = `autosync-${newVal}`;
    const performToggle = () => {
      db.settings.put({ key: 'autoSync', value: newVal })
        .then(() => setAutoSync(newVal))
        .catch(() => {
          toastService.error('Failed to update auto sync setting');
        })
        .finally(() => setLoadingAutoSync(false));
    };

    confirmationService.register(confirmId, performToggle, () => setLoadingAutoSync(false));

    dispatch(openConfirmationModal({
      title: newVal ? 'Enable Auto Sync?' : 'Disable Auto Sync?',
      message: newVal ? 'Automatically sync data when online?' : 'You will need to sync manually. Are you sure?',
      type: 'sync',
      confirmId
    }));
  };

  const handleClearLocalData = () => {
    const confirmId = 'clear-data';
    confirmationService.register(confirmId, () => {
      const clearPromise = storageService.clearAllData();
      toastService.notifyPromise(clearPromise, {
        pending: 'Clearing data...',
        success: 'Local data cleared!',
        error: 'Failed to clear data.',
      }).then(success => {
        if (success) {
          fetchStorageInfo();
        }
      });
    });

    dispatch(openConfirmationModal({
      title: 'Clear Local Data?',
      message: 'This will remove all offline data. This action cannot be undone.',
      type: 'danger',
      confirmId
    }));
  };
  
  return {
    autoSync,
    storageInfo,
    loadingAutoSync,
    handleAutoSyncToggle,
    handleClearLocalData
  };
};